import fetchClient from './fetchClient';
import { errorHandler } from './utils';
import { offlineDb } from 'utils/offlineDb';


const config = () => {
    return {
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    };
};

const MAX_PRODUCTS_PAGE_SIZE = 500;

const extractProductsList = (payload: any): any[] => {
    if (!payload) return [];
    const list = payload?.products ?? payload?.docs ?? payload?.data ?? [];
    return Array.isArray(list) ? list : [];
};

// Client-side, accent-insensitive match used only for the OFFLINE fallback.
// When online the search is always performed server-side (see getProducts).
const matchesProductSearch = (product: any, search: string): boolean => {
    if (!search) return true;
    const normalize = (value: any) =>
        String(value ?? '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    const term = normalize(search);
    return (
        normalize(product?.name).includes(term) ||
        normalize(product?.ref).includes(term) ||
        normalize(product?.ean).includes(term)
    );
};

export const getProducts = async (
    page: number = 1,
    limit: number = 100,
    customerId: string = null,
    search: string = '',
) => {
    const requestedLimit = Number.isFinite(limit) && limit > 0 ? limit : 100;
    const searchTerm = (search || '').trim();
    // A search is always resolved with a SINGLE bounded request: the server looks
    // through the whole catalog (indexed) and returns only the matches, so a
    // customer with thousands of products can find any item without us ever
    // loading the full list into memory.
    const shouldFetchAllPages =
        !searchTerm && page === 1 && requestedLimit > MAX_PRODUCTS_PAGE_SIZE;

    const buildUrl = (pageValue: number, limitValue: number) => {
        const params = new URLSearchParams();
        if (customerId) params.set('customerId', customerId);
        params.set('page', String(pageValue));
        params.set('limit', String(limitValue));
        if (searchTerm) params.set('search', searchTerm);
        return `/products?${params.toString()}`;
    };

    const fetchPage = async (pageValue: number, limitValue: number) => {
        return fetchClient
            .get(buildUrl(pageValue, limitValue), config())
            .catch((): null => null);
    };

    if (shouldFetchAllPages) {
        const allProducts: any[] = [];
        let currentPage = 1;
        let hasNextPage = true;
        let firstPayload: any = null;

        while (hasNextPage && allProducts.length < requestedLimit) {
            const pageRes: any = await fetchPage(currentPage, MAX_PRODUCTS_PAGE_SIZE);
            if (!pageRes?.data) break;

            const payload = pageRes.data;
            if (!firstPayload) firstPayload = payload;

            const pageProducts = extractProductsList(payload);
            allProducts.push(...pageProducts);

            const total = payload?.meta?.total;
            const hasNextFromApi = Boolean(payload?.next?.page);
            const reachedKnownTotal =
                typeof total === 'number' && allProducts.length >= total;

            if (hasNextFromApi || pageProducts.length === MAX_PRODUCTS_PAGE_SIZE) {
                hasNextPage = !reachedKnownTotal;
            } else {
                hasNextPage = false;
            }

            currentPage += 1;
        }

        if (allProducts.length > 0) {
            offlineDb.cacheProducts(allProducts).catch((): null => null);

            const limitedProducts = allProducts.slice(0, requestedLimit);
            return firstPayload
                ? { ...firstPayload, data: limitedProducts }
                : { data: limitedProducts };
        }
    }

    // Single request: either a normal page, or a server-side search (capped at
    // MAX_PRODUCTS_PAGE_SIZE matches which is plenty for a search dropdown).
    const effectiveLimit = searchTerm
        ? Math.min(requestedLimit, MAX_PRODUCTS_PAGE_SIZE)
        : requestedLimit;
    const res: any = await fetchPage(page, effectiveLimit);

    if (res?.data) {
        // Cache the response for offline use: both in the generic apiCache
        // (handled transparently by the SW fetch intercept) and in the
        // individual products store so getCachedProducts() can serve them.
        // We never evict on search results, we only add to the cache.
        const list: any[] = extractProductsList(res.data);
        if (Array.isArray(list) && list.length > 0) {
            // Non-blocking: fire and forget
            offlineDb.cacheProducts(list).catch((): null => null);
        }
        return res.data;
    }

    // Network unavailable — serve from the local products store.
    // The cache holds ALL products (from prefetch); if a customerId filter was
    // requested we apply it client-side so the UI still gets the right list.
    const cached = await offlineDb.getCachedProducts();
    if (cached && cached.length > 0) {
        let filtered = cached;
        if (customerId) {
            filtered = cached.filter((p: any) => {
                // A product is visible to a customer if it has no customer
                // restriction, or if it is explicitly linked to this customer
                if (!p.customersIds || p.customersIds.length === 0) return true;
                return p.customersIds.includes(customerId);
            });
            // If the filter returns nothing (restriction not stored), fall back
            // to the full list so the user is never left with an empty catalogue
            if (filtered.length === 0) filtered = cached;
        }
        // Apply the same search offline so the behaviour is consistent
        if (searchTerm) {
            filtered = filtered.filter((p: any) => matchesProductSearch(p, searchTerm));
        }
        // Wrap in the same shape the views expect: { data: [...] }
        return { data: filtered };
    }

    return null;
};

export const getProductsPurchasePrice = async (products: string[] = null) => {
    const res: any = await fetchClient
        .get(
            `/products/populated?limit=5000&productIds=${products.join(',')}`,
            config(),
        )
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const getProductsAlreadyOrdered = async (customerId: string = null) => {
    const res: any = await fetchClient
        .get(`/products/already-ordered?customerId=${customerId}`, config())
        .catch((): null => null);

    if (res?.data && Array.isArray(res.data)) {
        // Cache the results for offline use
        offlineDb.cacheProducts(res.data).catch((): null => null);
        return res.data;
    }

     // Offline fallback: best-effort list from cached products
    const cached = await offlineDb.getCachedProducts();
    if (cached && cached.length > 0) return cached;
     return [];
};

export const getProductsVisibility = async (customerId: string = null, page: number = 1, limit: number = 100) => {
    const requestedLimit = Number.isFinite(limit) && limit > 0 ? limit : 100;
    const shouldFetchAllPages = page === 1 && requestedLimit > MAX_PRODUCTS_PAGE_SIZE;

    const buildUrl = (pageValue: number, limitValue: number) =>
        `/products/visibility/${customerId}?page=${pageValue}&limit=${limitValue}`;

    const fetchPage = async (pageValue: number, limitValue: number) => {
        return fetchClient
            .get(buildUrl(pageValue, limitValue), config())
            .catch((): null => null);
    };

    if (shouldFetchAllPages) {
        const allProducts: any[] = [];
        let currentPage = 1;
        let hasNextPage = true;
        let firstPayload: any = null;

        while (hasNextPage && allProducts.length < requestedLimit) {
            const pageRes: any = await fetchPage(currentPage, MAX_PRODUCTS_PAGE_SIZE);
            if (!pageRes?.data) break;

            const payload = pageRes.data;
            if (!firstPayload) firstPayload = payload;

            const pageProducts = payload?.products || [];
            allProducts.push(...pageProducts);

            hasNextPage = Boolean(payload?.meta?.hasNextPage);
            currentPage += 1;
        }

        if (allProducts.length > 0 && firstPayload) {
            const limitedProducts = allProducts.slice(0, requestedLimit);
            return {
                ...firstPayload,
                products: limitedProducts,
                meta: {
                    ...firstPayload.meta,
                    totalProducts: allProducts.length,
                    hasNextPage: allProducts.length > requestedLimit,
                },
            };
        }
    }

    const res: any = await fetchPage(page, requestedLimit);
    if (!res) return null;
    return res.data;
};

export const getProduct = async (id: string) => {
    const res = await fetchClient.get(`/products/${id}`, config()).catch((err) => {
        return null;
    });
    if (!res) return null;
    return res.data;
};

export const updateProduct = async (product: any) => {
    const id = product._id;
    delete product._id;
    const res: any = await fetchClient
        .put(`/products/${id}`, product, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductCategory = async (product: any) => {
    const id = product._id;
    delete product._id;
    const res: any = await fetchClient
        .put(`/products/${id}/category`, product, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductVisibility = async (
    productId: string,
    customerId: string,
) => {
    const res: any = await fetchClient
        .put(`/products/visibility/${productId}/${customerId}`, {}, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductVisibilityBulk = async (
    productsIds: string[],
    customersIds: string[],
    newState: string,
) => {
    const res: any = await fetchClient
        .put(
            `/products/visibility-bulk`,
            {
                productsIds,
                customersIds,
                newState,
            },
            config(),
        )
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const duplicateProduct = async (productId: any) => {
    const res: any = await fetchClient
        .get(`/products/duplicate/${productId}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const addProductImage = async (id: string, image: any) => {
    const res: any = await fetchClient
        .post(`/products/${id}/image`, image, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const updateProductImageOrder = async (productId: string, data: { imageUrls: string[] }) => {
    const res: any = await fetchClient
        .put(`/products/${productId}/images/reorder`, data, config())
        .catch(errorHandler);
    if (res?.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const deleteProduct = async (id: string) => {
    const res: any = await fetchClient
        .delete(`/products/${id}`, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const archiveProduct = async (id: string) => {
    const res: any = await fetchClient
        .patch(`/products/${id}/archive`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const unarchiveProduct = async (id: string) => {
    const res: any = await fetchClient
        .patch(`/products/${id}/unarchive`, {}, config())
        .catch(errorHandler);
    if (res.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getArchivedProducts = async (): Promise<{ data: any; error: any }> => {
    const res: any = await fetchClient
        .get('/products/archived', config())
        .catch(errorHandler);
    if (res.error) return res;
    
    const data = res?.data || null;
    if (data && Array.isArray(data)) {
        // Cache archived products for offline use
        offlineDb.cacheProducts(data).catch((): null => null);
    }
    
    return { data, error: null };
};

export const deleteProductImage = async (productId: string, data: { imageUrl: string, index: number }) => {
    const res: any = await fetchClient
        .delete(`/products/${productId}/image`, { 
            data,
            ...config() 
        })
        .catch(errorHandler);
    if (res?.error) return res;
    return { data: res ? res.data : null, error: null };
};

export const getRecipe = async (productId: string) => {
    const res: any = await fetchClient
        .get(`/products/ingredients/${productId}`, config())
        .catch((err) => {
            return null;
        });
    if (!res) return null;
    return res.data;
};

export const createProduct = async (ingredient: any) => {
    const res = await fetchClient
        .post('/products', ingredient, config())
        .catch(errorHandler);
    return res.data;
};

export const getProductsByIds = async (ids: string[]) => {
    if (!Array.isArray(ids) || ids.length === 0) return [];
    // Tentative endpoint bulk
    const bulk = await fetchClient
      .post('/products/bulk', { ids }, config())
      .catch(() => null);
    if (bulk?.data) return bulk.data;
  
    // Fallback séquentiel si pas d’endpoint bulk
    const results: any[] = [];
    await Promise.all(
      ids.map(async (id) => {
        const one = await fetchClient.get(`/products/${id}`, config()).catch(() => null);
        if (one?.data) results.push(one.data);
      }),
    );
    return results;
  };

  export const updateProductPrice = async (productId: any, newPrice: any) => {
    try {
      const res = await fetchClient.put(
        `/products/${productId}/update-price`,
        { purchasePrice: newPrice },
        config()
      );
      return res.data;
    } catch (err) {
      console.error('Erreur mise à jour du prix produit', err);
      return { error: true };
    }
  };

export const updateProductSellingPrice = async (productId: string, pct: number) => {
    const res: any = await fetchClient
        .patch(`/products/${productId}/selling-price`, { pct }, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const removeChildFromParent = async (parentId: string, childId: string) => {
   const res: any = await fetchClient
    .patch(`/products/${parentId}/remove-child/${childId}`, {}, config())
    .catch(errorHandler);
  if (!res) return null;
  return res.data;
};

export const getProductsBySubCategory = async (subCategoryName: string) => {
    const res: any = await fetchClient
        .get(`/products/by-subcategory/${encodeURIComponent(subCategoryName)}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const resetProductsSubCategory = async (subCategoryName: string) => {
    const res: any = await fetchClient
        .put(`/products/reset-subcategory`, { subCategoryName }, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const getProductsByCategory = async (categoryId: string) => {
    const res: any = await fetchClient
        .get(`/products/by-category/${encodeURIComponent(categoryId)}`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const resetProductsCategory = async (categoryId: string) => {
    const res: any = await fetchClient
        .put(`/products/reset-category`, { categoryId }, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

export const downloadAllProducts = async () => {
    try {
        const res = await fetchClient.get('/products/export/csv', {
            ...config(),
            responseType: 'blob',
        });

        if (!res || !res.data) return null;

        const cd = (res.headers?.['content-disposition'] || '') as string;
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd || '');
        const filename = decodeURIComponent(match?.[1] || match?.[2] || 'produits.xlsx');

        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return true;
    } catch (err: any) {
        try {
            const blob = err?.response?.data as Blob | undefined;
            const text = blob ? await blob.text() : '';
            if (text) {
                const json = JSON.parse(text);
                errorHandler({ response: { data: { error: json?.error || 'Export impossible' } } });
            } else {
                errorHandler(err);
            }
        } catch {
            errorHandler(err);
        }
        return null;
    }
};

export const getProductPriceHistory = async (productId: string) => {
    const res: any = await fetchClient
        .get(`/products/${productId}/price-history`, config())
        .catch(errorHandler);
    if (!res) return null;
    return res.data;
};

